// Password fields, and the modal that asks for a password before a sensitive action (ADR 0030): the action runs from
// inside the dialog, a refused password stays in it with the server's message, and it closes only once the action went
// through. Shared by the sign-in forms, the account card and the deletion dialog.
import { ApiError } from '../../data/api.js';
import { busyWhile, esc, modal } from '../dom.js';
import { icon } from '../brand.js';
import { t } from '../../i18n/index.js';
import { errorText } from '../../i18n/content.js';

/** A password field with its show/hide toggle; `bindPasswordToggles` wires it. */
export const passwordField = (name: string, label: string, autocomplete: 'current-password' | 'new-password', minlength: number) =>
  `<label class="f"><span>${esc(label)}</span><span class="pw-field"><input name="${name}" type="password" autocomplete="${autocomplete}" minlength="${minlength}" required><button type="button" class="pw-toggle" data-pw aria-label="${esc(t.account.showPassword)}" title="${esc(t.account.showPassword)}">${icon('eye')}</button></span></label>`;
export function bindPasswordToggles(root: ParentNode) {
  root.querySelectorAll<HTMLButtonElement>('[data-pw]').forEach(b => (b.onclick = () => {
    const input = b.parentElement!.querySelector('input')!, show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    b.innerHTML = icon(show ? 'eyeoff' : 'eye');
    b.title = b.ariaLabel = show ? t.account.hidePassword : t.account.showPassword;
    input.focus();
  }));
}

export interface PasswordDialogField { name: string; label: string; autocomplete: 'current-password' | 'new-password'; minlength?: number }
export interface PasswordDialogOptions {
  title: string;
  text?: string;
  fields: PasswordDialogField[];
  okLabel: string;
  danger?: boolean;
  /** Runs with what was typed, by field name; what it throws is shown in the dialog, which stays open. */
  submit: (values: Record<string, string>) => Promise<void>;
}

/** Resolves true once `submit` went through, false when the player closed the dialog instead. */
export function passwordDialog({ title, text, fields, okLabel, danger, submit }: PasswordDialogOptions): Promise<boolean> {
  return new Promise(res => {
    let done = false;
    const m = modal(`<h2>${esc(title)}</h2>${text ? `<p class="mut">${esc(text)}</p>` : ''}
      <form class="stack mt-3" data-pw-form>
        ${fields.map(f => passwordField(f.name, f.label, f.autocomplete, f.minlength ?? 1)).join('')}
        <div class="bad small auth-err" role="alert" data-pw-err></div>
        <div class="actions"><button type="button" data-close>${esc(t.common.cancel)}</button><button type="submit" class="${danger ? 'danger' : 'primary'}">${esc(okLabel)}</button></div>
      </form>`, { onClose: () => res(done) });
    const root = m.el as HTMLElement;
    bindPasswordToggles(root);
    const form = root.querySelector<HTMLFormElement>('[data-pw-form]')!, err = root.querySelector<HTMLElement>('[data-pw-err]')!;
    form.querySelector('input')?.focus();
    form.onsubmit = (e: SubmitEvent) => {
      e.preventDefault();
      const f = new FormData(form), values: Record<string, string> = {};
      for (const x of fields) values[x.name] = String(f.get(x.name) ?? '');
      const btn = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
      err.textContent = '';
      return busyWhile(btn, async () => {
        try { await submit(values); done = true; m.close(); }
        catch (ex) { err.textContent = ex instanceof ApiError && ex.offline ? t.account.offline : errorText((ex as Error).message); }
      });
    };
  });
}
